import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { useCreateGoldenAuditDefinitionMutation } from "../../store/api/audit/goldenAuditApiSlice";
import { useGetTemplatesQuery } from "../../store/api/tasks/taskApiSlice";
import { ITemplates } from "../../store/api/tasks/types";
import { useStoreDispatch } from "../../store/hooks";
import { setBasicDialogOpenFor, setOpenToast } from "../../store/helpers/helperReducers";
import { convertSymbols, errorMessage } from "../../utils/helpers";
import { selectProfilePermissionsSelector } from "../../store/api/auth/selectors";
import ProfilePermissions from "../../utils/ProfilePermissions";
import { prepareGlobalTemplatesDataGrid, prepareTemplateCellDataGrid } from "../../pages/Configuration/helpers";
import { RadixSelectControl } from "../../components/ui/radix-select";
import { Sheet } from "../../components/ui/sheet";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";

const AddAuditDefinitionDialog: React.FC = () => {
  const dispatch = useStoreDispatch();
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);

  // Reuse the same templates source as the Templates tab
  const { data: templates, isLoading: templatesLoading } = useGetTemplatesQuery({ filters: "page=0&size=100" });

  const displayTemplates: ITemplates[] = useMemo(() => {
    const rowsData = prepareTemplateCellDataGrid(templates || []);
    return prepareGlobalTemplatesDataGrid(rowsData);
  }, [templates]);

  const [selectedTemplateName, setSelectedTemplateName] = useState<string>("");
  const [selectedNodeType, setSelectedNodeType] = useState<string>("");
  const [selectedRadioTech, setSelectedRadioTech] = useState<string>("");
  const [selectedHour, setSelectedHour] = useState<string>("12");
  const [selectedMinute, setSelectedMinute] = useState<string>("00");
  const [selectedMeridiem, setSelectedMeridiem] = useState<string>("AM");
  const [selectedTimezone, setSelectedTimezone] = useState<string>("UTC");

  const [createDefinition, { isLoading: creating }] = useCreateGoldenAuditDefinitionMutation();

  const resetForm = () => {
    setSelectedTemplateName("");
    setSelectedNodeType("");
    setSelectedRadioTech("");
    setSelectedHour("12");
    setSelectedMinute("00");
    setSelectedMeridiem("AM");
    setSelectedTimezone("UTC");
  };

  const computeScheduleStartTime = (): number | undefined => {
    const hour = Number(selectedHour);
    const minute = Number(selectedMinute);

    if (!hour || Number.isNaN(hour) || Number.isNaN(minute)) {
      return undefined;
    }

    const now = new Date();

    let hour24 = hour % 12;
    if (selectedMeridiem === "PM") {
      hour24 += 12;
    }

    if (selectedTimezone === "UTC") {
      return Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        hour24,
        minute,
        0,
        0
      );
    }

    const localDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      hour24,
      minute,
      0,
      0
    );
    const localUtcBase = Date.UTC(
      localDate.getFullYear(),
      localDate.getMonth(),
      localDate.getDate(),
      hour24,
      minute,
      0,
      0
    );
    const localOffsetMinutes = localDate.getTimezoneOffset();

    return localUtcBase + localOffsetMinutes * 60 * 1000;
  };

  const hourOptions = useMemo(() => {
    return Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, "0"));
  }, []);

  const minuteOptions = useMemo(() => {
    return Array.from({ length: 12 }, (_, index) => String(index * 5).padStart(2, "0"));
  }, []);

  const localTimeZone = useMemo(() => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }, []);

  const localTimeZoneLabel = useMemo(() => {
    return localTimeZone ? `Local (${localTimeZone})` : "Local";
  }, [localTimeZone]);

  if (!permissions?.hasOpCodeGroup("manage_topology")) {
    return null;
  }

  const handleSave = async () => {
    if (!selectedTemplateName || !selectedNodeType || !selectedRadioTech) {
      return;
    }

    const payload = {
      templateName: selectedTemplateName,
      scopeType: "GLOBAL_BY_TYPE_TECH",
      scheduleEnabled: true,
      nodeType: selectedNodeType,
      radioTech: selectedRadioTech,
      scheduleStartTime: computeScheduleStartTime(),
    };

    try {
      await createDefinition(payload).unwrap();
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "success",
          message: "Audit definition has been created successfully",
        })
      );
      resetForm();
      dispatch(setBasicDialogOpenFor(undefined));
    } catch (error: any) {
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "error",
          message: errorMessage(error),
        })
      );
    }
  };

  const canSave = !!selectedTemplateName && !!selectedNodeType && !!selectedRadioTech && !creating;

  const handleClose = () => {
    resetForm();
    dispatch(setBasicDialogOpenFor(undefined));
  };

  const footer = (
    <>
      <Button variant="outline" onClick={handleClose} disabled={creating}>
        Cancel
      </Button>
      <Button onClick={handleSave} disabled={!canSave}>
        Save
      </Button>
    </>
  );

  return (
    <Sheet open={true} onClose={handleClose} title="Add Configuration" footer={footer} width="520px">
      <div className="space-y-5 pt-2">
          <div className="space-y-2">
            <Label className="mb-3">Node Type</Label>
            <RadixSelectControl
              value={selectedNodeType || "__placeholder__"}
              onValueChange={(val) => setSelectedNodeType(val === "__placeholder__" ? "" : val)}
              options={[
                { value: "__placeholder__", label: "Select Node Type" },
                { value: "SN", label: "SN" },
                { value: "CU", label: "CU" },
                { value: "RCP", label: "RCP" },
                { value: "ME1200", label: "ME1200" },
              ]}
              placeholder="Select Node Type"
            />
          </div>

          <div className="space-y-2">
            <Label className="mb-3">Technology</Label>
            <RadixSelectControl
              value={selectedRadioTech || "__placeholder__"}
              onValueChange={(val) => setSelectedRadioTech(val === "__placeholder__" ? "" : val)}
              options={[
                { value: "__placeholder__", label: "Select Technology" },
                { value: "LTEONLY", label: "LTE" },
                { value: "NR", label: "NR" },
              ]}
              placeholder="Select Technology"
            />
          </div>

          <div className="space-y-2">
            <Label className="mb-3">Config Template</Label>
            <RadixSelectControl
              value={selectedTemplateName || "__placeholder__"}
              onValueChange={(val) => setSelectedTemplateName(val === "__placeholder__" ? "" : val)}
              options={[
                { value: "__placeholder__", label: "Select Template" },
                ...displayTemplates.map((tpl) => ({
                  value: tpl.templateName || "",
                  label: convertSymbols(tpl.templateName || ""),
                })),
              ]}
              placeholder="Select Template"
              disabled={templatesLoading}
            />
          </div>

          <div className="space-y-2 mt-2">
            <Label className="mb-3">Check daily at:</Label>
            <div className="flex items-center gap-4">
              <div style={{ minWidth: 80 }}>
                <RadixSelectControl
                  value={selectedHour}
                  onValueChange={setSelectedHour}
                  options={hourOptions.map((hour) => ({ value: hour, label: hour }))}
                  placeholder="HH"
                />
              </div>
              <div style={{ minWidth: 80 }}>
                <RadixSelectControl
                  value={selectedMinute}
                  onValueChange={setSelectedMinute}
                  options={minuteOptions.map((minute) => ({ value: minute, label: minute }))}
                  placeholder="MM"
                />
              </div>
              <div style={{ minWidth: 90 }}>
                <RadixSelectControl
                  value={selectedMeridiem}
                  onValueChange={setSelectedMeridiem}
                  options={[
                    { value: "AM", label: "AM" },
                    { value: "PM", label: "PM" },
                  ]}
                  placeholder="AM/PM"
                />
              </div>
              <div style={{ minWidth: 135 }}>
                <RadixSelectControl
                  value={selectedTimezone}
                  onValueChange={setSelectedTimezone}
                  options={[
                    { value: "LOCAL", label: localTimeZoneLabel },
                    { value: "UTC", label: "UTC" },
                  ]}
                  placeholder="Timezone"
                />
              </div>
            </div>
          </div>
        </div>
    </Sheet>
  );
};

export default AddAuditDefinitionDialog;
